import { createFilterOptions } from "@mui/base";
import {
  Autocomplete,
  AutocompleteProps,
  Chip,
  Divider,
  Paper,
  PaperProps,
  Popper,
  PopperProps,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { InputBaseProps } from "@mui/material/InputBase";
import { OutlinedInputProps } from "@mui/material/OutlinedInput";
import { Box } from "@mui/system";
import { SEARCH_FIELD_LIMIT } from "@sinch/core/config/globals";
import { MdsAdd, MdsClose, MdsCloseXs, MdsExpandMore } from "@sinch/icons";
import { find, isEmpty, omit, prop, propEq, take } from "ramda";
import React, { ForwardedRef, forwardRef, useId, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { MdsIcon } from "../../MdsIcon/MdsIcon";
import { IconTypography } from "../../typography/IconTypography/IconTypography";
import { FormControlWrapper } from "../FormControlWrapper/FormControlWrapper";
import { InputSkeleton } from "../InputSkeleton/InputSkeleton";
import { useAutocompleteInput } from "./useAutocompleteInput";
import {
  AutocompleteAddOptionProps,
  AutocompleteInputProps,
  AutocompleteOptions,
} from "@sinch/components/form/AutocompleteInput/types";

const AddOption = ({ icon, label, ...props }: AutocompleteAddOptionProps) => (
  <IconTypography
    {...props}
    icon={icon}
    iconColor={(theme) => theme.palette.info.main}
    sx={{ color: (theme) => theme.palette.info.main }}
  >
    {label}
  </IconTypography>
);

function AutocompleteInputBase<
  T extends AutocompleteOptions = AutocompleteOptions,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  {
    label,
    belowLabel,
    FormHelperTextProps: formHelperTextProps,
    FormControlProps: formControlProps,
    helperText,
    errorMessage,
    error,
    required,
    infoTooltip,
    multiple,
    options = [],
    onChange,
    freeSolo,
    value,
    placeholder,
    fullWidth,
    size,
    paperWidth = "100%",
    textFieldProps,
    skeleton,
    inputReadOnly,
    disablePortal = true,
    onAddAction,
    slots,
    dropdownWidth,
    addActionPosition = "start",
    renderOption,
    limitCount,
    disableCloseOnSelect,
    inputValue,
    onFocus,
    onInputChange,
    onSearch,
    ...props
  }: AutocompleteInputProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<unknown>
) {
  const { t } = useTranslation();
  const addOptionId = useId();
  const noOptionsId = useId();
  const [searchValue, setSearchValue] = useState<string>("");
  const searchLimit = limitCount ?? SEARCH_FIELD_LIMIT;
  // a multiple select reports how many records are picked next to the count of shown ones
  const selectedCount = multiple && Array.isArray(value) ? value.length : 0;
  // filterOptions runs while the Autocomplete renders, before the dropdown paper does, so the paper reads
  // the result info of the current render from this holder instead of forcing another one
  const searchResultsRef = useRef<SearchResults>({ shown: 0, limited: false });
  // only the search paper knows these props, the plain MUI paper would spill them into the DOM
  const searchPaperProps = onSearch ? { searchResults: searchResultsRef, selectedCount } : {};
  // "search" reaches records that are not loaded yet, which only a server backed search does,
  // anything else is picking from a list that is already there
  const inputPlaceholder = placeholder ?? (onSearch ? t("search") : t("select"));

  const AddOptionSlot = slots?.addOption ?? AddOption;
  const addOptionProps = useMemo(
    () => ({ icon: MdsAdd, label: t("createNew"), ...props.slotProps?.addOption }),
    [props.slotProps?.addOption, t]
  );
  const addOptionValue = props.slotProps?.addOption?.value ?? addOptionId;
  const addOption = {
    value: addOptionValue,
    label: addOptionProps.label,
    option: <AddOptionSlot {...addOptionProps} />,
  };

  const noOptions = {
    value: noOptionsId,
    label: t("noOptions"),
    option: <AutocompleteNoOptions>{t("noOptions")}</AutocompleteNoOptions>,
  };

  const {
    onChange: handleChange,
    onInputChange: handleInputChange,
    value: scalarValue,
    minWidth: minInputWidthForMultiple,
  } = useAutocompleteInput<T, Multiple, DisableClearable, FreeSolo>({
    options,
    value,
    multiple,
    onChange,
    freeSolo,
    placeholder: inputPlaceholder,
    onAddAction,
    addOptionValue,
  });

  const handleAutocompleteInputChange: NonNullable<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>["onInputChange"]
  > = (event, newInputValue, reason) => {
    if (!onSearch) {
      // outside the search mode the internal handler keeps its state in sync (the typed text feeds
      // onAddAction and the freeSolo value) and the consumer handler runs on top of it
      handleInputChange(event, newInputValue, reason);
      onInputChange?.(event, newInputValue, reason);
      return;
    }

    handleInputChange(event, newInputValue, reason);

    // a value reset arrives without a user event, it carries the label of the selected option which
    // a single select shows in its input, but it never starts a search
    if (!event) {
      if (!multiple) {
        setSearchValue(newInputValue);
      }
      return;
    }

    // removing a chip reports an empty text, that may not wipe the text the user is typing right now
    if (multiple && reason === "removeOption") {
      return;
    }

    // picking an option in multiple mode arrives with an empty text from MUI, so the confirmed search
    // is cleared and the next one starts from scratch
    setSearchValue(newInputValue);
    onSearch(newInputValue);
  };

  const extendedOptions = useMemo(() => {
    if (onAddAction && addActionPosition === "start") {
      return [addOption, ...options];
    } else if (onAddAction && addActionPosition === "end") {
      return [...options, addOption];
    } else {
      return options;
    }
  }, [onAddAction, options, addOptionProps, AddOptionSlot, t]);

  return (
    <FormControlWrapper
      belowLabel={belowLabel}
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      fullWidth={fullWidth}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      {...formControlProps}
    >
      <InputSkeleton active={skeleton} animation="wave" width="100%">
        <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
          disablePortal={disablePortal}
          fullWidth={fullWidth}
          getOptionKey={(option) => prop<string | number>("value", option) ?? (option as string | number)}
          getOptionLabel={(option) => prop("label", option) ?? ""}
          isOptionEqualToValue={(option, val) => prop("value", option) === prop("value", val)}
          multiple={multiple}
          onChange={handleChange}
          renderOption={(optionProps, option, ...rest) => {
            if (option.value === noOptionsId) {
              return option.option;
            }
            if (option.value === addOptionValue) {
              return <AutocompleteOption<T> {...optionProps} {...option} key={option.value} />;
            }
            return (
              renderOption?.(optionProps, option, ...rest) ?? (
                <AutocompleteOption<T> {...optionProps} {...option} key={option.value} />
              )
            );
          }}
          renderTags={(vals, getTagProps) =>
            vals.map((val, index) => (
              <Chip
                {...getTagProps({ index })}
                key={val?.value || index}
                deleteIcon={<MdsIcon fontSize="xs" icon={MdsCloseXs} />}
                label={val?.label}
                sx={{ borderRadius: "4px !important" }}
              />
            ))
          }
          selectOnFocus={props.readOnly || inputReadOnly ? false : undefined}
          slotProps={{
            // @ts-ignore extended property
            paper: { sx: { width: paperWidth }, "data-cy": "dropdownOptions", ...searchPaperProps },
            popper: {
              placement: "bottom-start",
              popperOptions: {
                strategy: "fixed",
                modifiers: [
                  {
                    name: "flip",
                    options: { padding: 32 },
                  },
                  {
                    name: "offset",
                    options: {
                      offset: [0, 2],
                    },
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "window",
                    },
                  },
                ],
              },
            },
          }}
          sx={{
            pointerEvents: props.readOnly ? "none" : undefined,
          }}
          {...omit(["inputRef"], props)}
          ref={ref}
          clearIcon={<MdsIcon fontSize="small" icon={MdsClose} />}
          // picking an option out of a multiple select is rarely the last one, so the dropdown stays open
          disableCloseOnSelect={disableCloseOnSelect ?? multiple}
          filterOptions={(filterOptions, state) => {
            if (props.loading) {
              searchResultsRef.current = { shown: 0, limited: false };
              return [];
            }

            const filtered = (props.filterOptions || createFilterOptions<T>())(filterOptions, {
              ...state,
              inputValue: state.inputValue?.trim() ?? "",
            });
            // a search only ever shows the first records, the rest is reached by narrowing the search down.
            // A query loading with the same limit never returns more than it, so a full list means "there may be more"
            const opt = onSearch ? take(searchLimit, filtered) : filtered;
            searchResultsRef.current = { shown: opt.length, limited: filtered.length >= searchLimit };

            if (!isEmpty(opt) && onAddAction && !find(propEq("value", addOptionValue), opt)) {
              return [addOption, ...opt] as T[];
            } else if (isEmpty(opt) && onAddAction) {
              return [addOption, noOptions] as T[];
            }
            return opt as T[];
          }}
          freeSolo={freeSolo}
          inputValue={onSearch ? searchValue : inputValue}
          onFocus={(event) => {
            onSearch?.(searchValue);
            onFocus?.(event);
          }}
          onInputChange={handleAutocompleteInputChange}
          options={extendedOptions as readonly T[]}
          PopperComponent={dropdownWidth === "input-width" ? undefined : PopperContentWidth}
          popupIcon={props.popupIcon ?? <MdsIcon fontSize="small" icon={MdsExpandMore} />}
          renderInput={(params) => {
            const textFieldHtmlInputProps =
              (textFieldProps?.slotProps?.htmlInput as InputBaseProps["inputProps"]) ?? textFieldProps?.inputProps;
            const propsHtmlInputProps = params.inputProps;
            const textFieldInputProps =
              (textFieldProps?.slotProps?.input as Partial<OutlinedInputProps>) ?? textFieldProps?.InputProps;
            const paramsInputProps = params.InputProps;
            return (
              <TextField
                {...textFieldProps}
                {...params}
                error={error}
                inputRef={props.inputRef}
                placeholder={inputPlaceholder}
                required={required}
                slotProps={{
                  htmlInput: {
                    enterKeyHint: freeSolo && multiple ? "enter" : undefined,
                    ...textFieldHtmlInputProps,
                    ...propsHtmlInputProps,
                    readOnly: props.readOnly || inputReadOnly,
                  },
                  input: {
                    ...textFieldInputProps,
                    ...paramsInputProps,
                    startAdornment: (
                      <>
                        {size === "small" &&
                          !paramsInputProps.startAdornment &&
                          !textFieldInputProps?.startAdornment && (
                            <Box
                              sx={{
                                paddingLeft: 1.25,
                              }}
                            />
                          )}
                        {paramsInputProps.startAdornment
                          ? paramsInputProps.startAdornment
                          : textFieldInputProps?.startAdornment}
                      </>
                    ),
                    endAdornment: !props.readOnly ? (
                      <>
                        {textFieldInputProps?.endAdornment}
                        {paramsInputProps.endAdornment} {/* The "clear X" */}
                      </>
                    ) : undefined,
                    notched: false,
                    size: size,
                  },
                }}
                sx={() => ({
                  // When multiple calculate min width by placeholder
                  "& .MuiAutocomplete-input.MuiInputBase-input": {
                    minWidth: minInputWidthForMultiple,
                  },
                })}
                variant="outlined"
              />
            );
          }}
          slots={onSearch ? { paper: SearchResultsPaper } : undefined}
          value={scalarValue}
        />
      </InputSkeleton>
    </FormControlWrapper>
  );
}

/**
 * Autocomplete input, behave as select
 *
 * A multiple select keeps its dropdown open after an option is picked, pass `disableCloseOnSelect={false}`
 * to close it on the first pick.
 *
 * For documentation see [https://mui.com/material-ui/api/autocomplete/](https://mui.com/material-ui/api/autocomplete/)
 */
export const AutocompleteInput = forwardRef(AutocompleteInputBase) as <
  T extends AutocompleteOptions = AutocompleteOptions,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: AutocompleteInputProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: ForwardedRef<HTMLDivElement | undefined> | undefined;
  }
) => ReturnType<typeof AutocompleteInputBase>;

export const AutocompleteOption = <T extends AutocompleteOptions = AutocompleteOptions>({
  value: optionValue,
  option,
  label: optionLabel,
  ...optionProps
}: React.HTMLAttributes<HTMLLIElement> & T) => (
  <li
    data-cy={`dropdownOption-${optionProps?.["data-option-index" as keyof React.HTMLAttributes<HTMLLIElement>]}`}
    {...optionProps}
    key={optionValue}
    style={{ whiteSpace: "nowrap" }}
  >
    {option ?? (
      <Box
        component="span"
        sx={{
          overflowX: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {optionLabel}
      </Box>
    )}
  </li>
);

const AutocompleteNoOptions = styled("div", {
  name: "MuiAutocomplete",
  slot: "NoOptions",
  overridesResolver: (props, styles) => styles.noOptions,
})(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: "14px 16px",
  margin: "0 8px",
}));

export const PopperContentWidth = (props: PopperProps) => <Popper {...props} sx={{ minWidth: "fit-content" }} />;

interface SearchResults {
  /**
   * Count of options the dropdown shows right now
   */
  shown: number;
  /**
   * True when the search found more records than the dropdown shows
   */
  limited: boolean;
}

/**
 * Dropdown paper of a search input, headed by the count of records the dropdown shows and, for a
 * multiple select, by the count of records that are picked
 */
export const SearchResultsPaper = ({
  children,
  searchResults,
  selectedCount = 0,
  ...props
}: PaperProps & { searchResults?: { current: SearchResults }; selectedCount?: number }) => {
  const { t } = useTranslation();
  const { shown, limited } = searchResults?.current ?? { shown: 0, limited: false };

  return (
    <Paper {...props}>
      {shown > 0 && (
        <>
          <Typography
            component="div"
            sx={({ palette }) => ({
              pr: 2,
              pt: 1,
              pb: 0.5,
              pl: 2.5,
              color: palette.grey[600],
            })}
            variant="descriptionTiny"
          >
            {limited ? t("showingTopResults", { count: shown }) : t("showingResults", { count: shown })}
            {selectedCount > 0 && `, ${t("selectedCount", { count: selectedCount })}`}
          </Typography>
          <Divider />
        </>
      )}
      {children}
    </Paper>
  );
};
